/**
 * @file create campaign config
 * @author caiguoqing
 */
import React from "react";
import {
  AudienceSizeLargeIcon,
  AudienceSizeMediumIcon,
  AudienceSizeSmallIcon,
  PriorityHighlyIcon,
  PriorityPrioritizedIcon,
} from "@/components/icons";
import capitalizeFirstLetter from "@/utils/capitalizeFirstLetter";
import { Tooltip } from "antd";
import i18n from "i18next";

// 这个枚举一定要按照创建步骤顺序写，要不然会影响每一步上面的展示
export enum CreateCampaignStageEnum {
  BUSINESS_INFORMATION = "key_message",
  SETTINGS = "setting",
  BUDGET_BIDDING = "budget",
  PREVIEW_SAMPLE_MATCHES = "preview_sample_matches",
}

export enum AdsCampaignMarketingStrategy {
  INFLUENCER_MARKETING = "influencer_marketing",
  AFFILIATES_MARKETING = "affiliates_marketing",
  SALES_OUTREACH = "sales_outreach",
}

export enum ProductType {
  WEB_APP = "web app",
  MOBILE_APP = "mobile app",
  DESKTOP_APP = "desktop app",
  BROWSER_EXTENSION = "browser extension",
  PHYSICAL_PRODUCT = "physical product",
}

export const clientTypeOptions = Object.entries(ProductType).map(
  ([label, value]) => {
    return {
      label: capitalizeFirstLetter(value),
      value,
    };
  },
);

export enum AdsCampaignObjective {
  AWARENESS = "Awareness",
  WEBSITE_TRAFFIC = "Website Traffic",
  INSTALLS = "Installs",
  LEADS = "Leads",
  SIGN_UPS = "Sign-ups",
  SALES = "Sales",
}

export enum AdsCampaignObjectiveSubType {
  SUBSCRIPTION = "Subscription",
  PAY_AS_YOU_GO = "Pay-as-You-Go",
  ONE_TIME_PAYMENT = "One-time payment",
}

export enum PlatFormEnum {
  TWITTER = "twitter",
  X = "x",
  YOUTUBE = "youtube",
  TIKTOK = "tiktok",
  INSTAGRAM = "instagram",
  LINKEDIN = "linkedin",
}

export const selectPlatformOPtions = [
  {
    value: PlatFormEnum.YOUTUBE,
    label: "YouTube",
  },
  {
    value: PlatFormEnum.INSTAGRAM,
    label: "Instagram",
  },
  {
    value: PlatFormEnum.TIKTOK,
    label: "TikTok",
  },
  {
    value: PlatFormEnum.TWITTER,
    label: "Twitter",
  },
  {
    value: PlatFormEnum.LINKEDIN,
    label: "LinkedIn",
  },
];

export enum PlatformContentEnum {
  YOUTUBE_DEDICATED = "youtubeDedicated",
  YOUTUBE_INTEGRATED = "youtubeIntegrated",
  TIKTOK_VIDEO = "tiktokVideo",
  // TIKTOK_INTEGRATED_VIDEO = 'tiktokIntegratedVideo',
  INSTAGRAM_REEL = "instagramReel",
  // INSTAGRAM_INTEGRATED_REEL = 'instagramIntegratedReel',
  INSTAGRAM_CAROUSEL_POST = "instagramCarouselPost",
  // INSTAGRAM_INTEGRATED_CAROUSEL_POST = 'instagramIntegratedCarouselPost',
  LINKEDIN_POST = "linkedinPost",
  LINKEDIN_IMAGE_POST = "linkedinImagePost",
  LINKEDIN_VIDEO_POST = "linkedinVideoPost",
  TWITTER_POST = "twitterPost",
  // FACEBOOK_POST = 'facebookPost',
  // FACEBOOK_REEL = 'facebookReel',
}

enum PlatformContentLabelEnum {
  YOUTUBE_DEDICATED = "YouTube dedicated videos",
  YOUTUBE_INTEGRATED = "YouTube integrated videos",
  TIKTOK_VIDEO = "TikTok videos",
  TIKTOK_INTEGRATED_VIDEO = "TikTok integrated videos",
  INSTAGRAM_REEL = "Instagram Reels",
  INSTAGRAM_INTEGRATED_REEL = "Instagram integrated reels",
  INSTAGRAM_CAROUSEL_POST = "Instagram carousel posts",
  INSTAGRAM_INTEGRATED_CAROUSEL_POST = "Instagram integrated carousel posts",
  TWITTER_POST = "Twitter / X posts",
  LINKEDIN_IMAGE_POST = "LinkedIn image posts",
  LINKEDIN_VIDEO_POST = "LinkedIn video posts",
  LINKEDIN_POST = "LinkedIn posts",
  FACEBOOK_POST = "Facebook posts",
  FACEBOOK_REEL = "Facebook reels",
}

const getPlacementTooltipMap = (key: string, t?: any) => {
  return {
    [PlatformContentEnum.YOUTUBE_DEDICATED]: t(
      "campaignCreate.settings.placementsInfoModal.youtubeDedicatedDesc",
    ),
    [PlatformContentEnum.YOUTUBE_INTEGRATED]: t(
      "campaignCreate.settings.placementsInfoModal.youtubeIntegratedDesc",
    ),
    [PlatformContentEnum.TIKTOK_VIDEO]: t(
      "campaignCreate.settings.placementsInfoModal.tiktokVideoDesc",
    ),
    [PlatformContentEnum.INSTAGRAM_REEL]: t(
      "campaignCreate.settings.placementsInfoModal.instagramReelDesc",
    ),
    [PlatformContentEnum.INSTAGRAM_CAROUSEL_POST]: t(
      "campaignCreate.settings.placementsInfoModal.instagramCarouselPostDesc",
    ),
    [PlatformContentEnum.TWITTER_POST]: t(
      "campaignCreate.settings.placementsInfoModal.twitterPostDesc",
    ),
    [PlatformContentEnum.LINKEDIN_IMAGE_POST]: t(
      "campaignCreate.settings.placementsInfoModal.linkedinImagePostDesc",
    ),
    [PlatformContentEnum.LINKEDIN_VIDEO_POST]: t(
      "campaignCreate.settings.placementsInfoModal.linkedinVideoPostDesc",
    ),
    [PlatformContentEnum.LINKEDIN_POST]: t(
      "campaignCreate.settings.placementsInfoModal.linkedinPostDesc",
    ),
  }[key];
};

export const getPlatformContentLabelI18n = (key: string, t?: any) => {
  return {
    [PlatformContentEnum.YOUTUBE_DEDICATED]: t(
      "campaignCreate.platformContentLabel.youtubeDedicated",
    ),
    [PlatformContentEnum.YOUTUBE_INTEGRATED]: t(
      "campaignCreate.platformContentLabel.youtubeIntegrated",
    ),
    [PlatformContentEnum.TIKTOK_VIDEO]: t(
      "campaignCreate.platformContentLabel.tiktokVideo",
    ),
    [PlatformContentEnum.INSTAGRAM_REEL]: t(
      "campaignCreate.platformContentLabel.instagramReel",
    ),
    [PlatformContentEnum.INSTAGRAM_CAROUSEL_POST]: t(
      "campaignCreate.platformContentLabel.instagramCarouselPost",
    ),
    [PlatformContentEnum.TWITTER_POST]: t(
      "campaignCreate.platformContentLabel.twitterPost",
    ),
    [PlatformContentEnum.LINKEDIN_POST]: t(
      "campaignCreate.platformContentLabel.linkedinPost",
    ),
    [PlatformContentEnum.LINKEDIN_IMAGE_POST]: t(
      "campaignCreate.platformContentLabel.linkedinImagePost",
    ),
    [PlatformContentEnum.LINKEDIN_VIDEO_POST]: t(
      "campaignCreate.platformContentLabel.linkedinVideoPost",
    ),
  }[key];
};

export const getPlatformContentLabel = (key: string, t?: any) => {
  return {
    [PlatformContentEnum.YOUTUBE_DEDICATED]: getPlatformContentLabelI18n(
      key,
      t,
    ),
    [PlatformContentEnum.YOUTUBE_INTEGRATED]: getPlatformContentLabelI18n(
      key,
      t,
    ),
    [PlatformContentEnum.TIKTOK_VIDEO]: getPlatformContentLabelI18n(key, t),
    [PlatformContentEnum.INSTAGRAM_REEL]: getPlatformContentLabelI18n(key, t),
    [PlatformContentEnum.INSTAGRAM_CAROUSEL_POST]: getPlatformContentLabelI18n(
      key,
      t,
    ),
    [PlatformContentEnum.TWITTER_POST]: getPlatformContentLabelI18n(key, t),
    [PlatformContentEnum.LINKEDIN_POST]: getPlatformContentLabelI18n(key, t),
    [PlatformContentEnum.LINKEDIN_IMAGE_POST]: getPlatformContentLabelI18n(
      key,
      t,
    ),
    [PlatformContentEnum.LINKEDIN_VIDEO_POST]: getPlatformContentLabelI18n(
      key,
      t,
    ),
    // [PlatformContentEnum.TIKTOK_INTEGRATED_VIDEO]: PlatformContentLabelEnum.TIKTOK_INTEGRATED_VIDEO,
    // [PlatformContentEnum.INSTAGRAM_INTEGRATED_REEL]: PlatformContentLabelEnum.INSTAGRAM_INTEGRATED_REEL,
    // [PlatformContentEnum.INSTAGRAM_INTEGRATED_CAROUSEL_POST]: PlatformContentLabelEnum.INSTAGRAM_INTEGRATED_CAROUSEL_POST,
    // [PlatformContentEnum.FACEBOOK_POST]: PlatformContentLabelEnum.FACEBOOK_POST,
    // [PlatformContentEnum.FACEBOOK_REEL]: PlatformContentLabelEnum.FACEBOOK_REEL,
  }[key];
};

export const getPlatformContentOptions = (t?: any, placementsValues?: any) =>
  Object.entries(PlatformContentEnum)
    .filter(
      ([key, value]) =>
        value !== PlatformContentEnum.LINKEDIN_POST ||
        (value === PlatformContentEnum.LINKEDIN_POST &&
          placementsValues?.includes(PlatformContentEnum.LINKEDIN_POST)),
    )
    ?.map(([key, value]) => ({
      value: value,
      label: (
        <Tooltip
          overlayInnerStyle={{ width: "488px" }}
          title={
            value === PlatformContentEnum.TWITTER_POST
              ? t("campaignCreate.settings.twitterPostTooltip")
              : ""
          }
        >
          <div style={{ width: "100%", display: "inline-block" }}>
            {
              PlatformContentLabelEnum[
                key as keyof typeof PlatformContentLabelEnum
              ]
            }
          </div>
        </Tooltip>
      ),
    }));

export enum LanguageEnum {
  CHINESE = "zh",
  ENGLISH = "en",
  HINDI = "hi",
  SPANISH = "es",
  ARABIC = "ar",
  FRENCH = "fr",
  BENGALI = "bn",
  RUSSIAN = "ru",
  PORTUGUESE = "pt",
  URDU = "ur",
  INDONESIAN = "id",
  GERMAN = "de",
  JAPANESE = "ja",
  VIETNAMESE = "vi",
  KOREAN = "ko",
  TAMIL = "ta",
  TURKISH = "tr",
  ITALIAN = "it",
  THAI = "th",
  PERSIAN = "fa",
  POLISH = "pl",
  UKRAINIAN = "uk",
  BURMESE = "my",
  SWAHILI = "sw",
  PASHTO = "ps",
  AMHARIC = "am",
  ROMANIAN = "ro",
  DUTCH = "nl",
  SINHALA = "si",
  SWEDISH = "sv",
  CZECH = "cs",
  HUNGARIAN = "hu",
  AZERBAIJANI = "az",
  GREEK = "el",
  HEBREW = "he",
  NEPALI = "ne",
  KHMER = "km",
  ZULU = "zu",
  SOMALI = "so",
  FINNISH = "fi",
  BULGARIAN = "bg",
  SLOVAK = "sk",
  DANISH = "da",
  CATALAN = "ca",
  CROATIAN = "hr",
  DZONGKHA = "dz",
  ESTONIAN = "et",
  GEORGIAN = "ka",
  ICELANDIC = "is",
  IRISH = "ga",
  KAZAKH = "kk",
  KURDISH = "ku",
  KYRGYZ = "ky",
  LAO = "lo",
  LATIN = "la",
  LATVIAN = "lv",
  LITHUANIAN = "lt",
  MACEDONIAN = "mk",
  MALAGASY = "mg",
  MALAY = "ms",
  MALTESE = "mt",
  MONGOLIAN = "mn",
  NORWEGIAN = "no",
  SERBIAN = "sr",
  SLOVENIAN = "sl",
  TAJIK = "tg",
  TURKMEN = "tk",
  UZBEK = "uz",
  XHOSA = "xh",
  FILIPINO = "fil",
  DIVEHI = "dv",
  BELARUSIAN = "be",
  ARMENIAN = "hy",
  TIGRINYA = "ti",
  KINYARWANDA = "rw",
  KIRUNDI = "rn",
  CAPE_VERDEAN_CREOLE = "crs",
  NYANJA = "ny",
  SWATI = "ss",
  COMORIAN = "swb",
  SOUTHERN_SOTHO = "st",
  HAITIAN_CREOLE = "ht",
  LUXEMBOURGISH = "lb",
  ALBANIAN = "sq",
  MONTENEGRIN = "cnr",
  FAROESE = "fo",
  BOSNIAN = "bs",
  PALAUAN = "pau",
  MARSHALLESE = "mh",
  GILBERTESE = "gil",
  NAURU = "na",
  BISLAMA = "bi",
  FIJIAN = "fj",
  TUVALUAN = "tvl",
  SAMOAN = "sm",
  TONGAN = "to",
  RAROTONGAN = "rar",
  NIUEAN = "niu",
  TOKELAUAN = "tkl",
  GREENLANDIC = "kl",
}

export const languageLabel: { [key in LanguageEnum]: string } = {
  [LanguageEnum.CHINESE]: "Chinese",
  [LanguageEnum.ENGLISH]: "English",
  [LanguageEnum.HINDI]: "Hindi",
  [LanguageEnum.SPANISH]: "Spanish",
  [LanguageEnum.ARABIC]: "Arabic",
  [LanguageEnum.FRENCH]: "French",
  [LanguageEnum.BENGALI]: "Bengali",
  [LanguageEnum.RUSSIAN]: "Russian",
  [LanguageEnum.PORTUGUESE]: "Portuguese",
  [LanguageEnum.URDU]: "Urdu",
  [LanguageEnum.INDONESIAN]: "Indonesian",
  [LanguageEnum.GERMAN]: "German",
  [LanguageEnum.JAPANESE]: "Japanese",
  [LanguageEnum.VIETNAMESE]: "Vietnamese",
  [LanguageEnum.KOREAN]: "Korean",
  [LanguageEnum.TAMIL]: "Tamil",
  [LanguageEnum.TURKISH]: "Turkish",
  [LanguageEnum.ITALIAN]: "Italian",
  [LanguageEnum.THAI]: "Thai",
  [LanguageEnum.PERSIAN]: "Persian",
  [LanguageEnum.POLISH]: "Polish",
  [LanguageEnum.UKRAINIAN]: "Ukrainian",
  [LanguageEnum.BURMESE]: "Burmese",
  [LanguageEnum.SWAHILI]: "Swahili",
  [LanguageEnum.PASHTO]: "Pashto",
  [LanguageEnum.AMHARIC]: "Amharic",
  [LanguageEnum.ROMANIAN]: "Romanian",
  [LanguageEnum.DUTCH]: "Dutch",
  [LanguageEnum.SINHALA]: "Sinhala",
  [LanguageEnum.SWEDISH]: "Swedish",
  [LanguageEnum.CZECH]: "Czech",
  [LanguageEnum.HUNGARIAN]: "Hungarian",
  [LanguageEnum.AZERBAIJANI]: "Azerbaijani",
  [LanguageEnum.GREEK]: "Greek",
  [LanguageEnum.HEBREW]: "Hebrew",
  [LanguageEnum.NEPALI]: "Nepali",
  [LanguageEnum.KHMER]: "Khmer",
  [LanguageEnum.ZULU]: "Zulu",
  [LanguageEnum.SOMALI]: "Somali",
  [LanguageEnum.FINNISH]: "Finnish",
  [LanguageEnum.BULGARIAN]: "Bulgarian",
  [LanguageEnum.SLOVAK]: "Slovak",
  [LanguageEnum.DANISH]: "Danish",
  [LanguageEnum.CATALAN]: "Catalan",
  [LanguageEnum.CROATIAN]: "Croatian",
  [LanguageEnum.DZONGKHA]: "Dzongkha",
  [LanguageEnum.ESTONIAN]: "Estonian",
  [LanguageEnum.GEORGIAN]: "Georgian",
  [LanguageEnum.ICELANDIC]: "Icelandic",
  [LanguageEnum.IRISH]: "Irish",
  [LanguageEnum.KAZAKH]: "Kazakh",
  [LanguageEnum.KURDISH]: "Kurdish",
  [LanguageEnum.KYRGYZ]: "Kyrgyz",
  [LanguageEnum.LAO]: "Lao",
  [LanguageEnum.LATIN]: "Latin",
  [LanguageEnum.LATVIAN]: "Latvian",
  [LanguageEnum.LITHUANIAN]: "Lithuanian",
  [LanguageEnum.MACEDONIAN]: "Macedonian",
  [LanguageEnum.MALAGASY]: "Malagasy",
  [LanguageEnum.MALAY]: "Malay",
  [LanguageEnum.MALTESE]: "Maltese",
  [LanguageEnum.MONGOLIAN]: "Mongolian",
  [LanguageEnum.NORWEGIAN]: "Norwegian",
  [LanguageEnum.SERBIAN]: "Serbian",
  [LanguageEnum.SLOVENIAN]: "Slovenian",
  [LanguageEnum.TAJIK]: "Tajik",
  [LanguageEnum.TURKMEN]: "Turkmen",
  [LanguageEnum.UZBEK]: "Uzbek",
  [LanguageEnum.XHOSA]: "Xhosa",
  [LanguageEnum.FILIPINO]: "Filipino",
  [LanguageEnum.DIVEHI]: "Divehi",
  [LanguageEnum.BELARUSIAN]: "Belarusian",
  [LanguageEnum.ARMENIAN]: "Armenian",
  [LanguageEnum.TIGRINYA]: "Tigrinya",
  [LanguageEnum.KINYARWANDA]: "Kinyarwanda",
  [LanguageEnum.KIRUNDI]: "Kirundi",
  [LanguageEnum.CAPE_VERDEAN_CREOLE]: "Cape Verdean Creole",
  [LanguageEnum.NYANJA]: "Nyanja",
  [LanguageEnum.SWATI]: "Swati",
  [LanguageEnum.COMORIAN]: "Comorian",
  [LanguageEnum.SOUTHERN_SOTHO]: "Southern Sotho",
  [LanguageEnum.HAITIAN_CREOLE]: "Haitian Creole",
  [LanguageEnum.LUXEMBOURGISH]: "Luxembourgish",
  [LanguageEnum.ALBANIAN]: "Albanian",
  [LanguageEnum.MONTENEGRIN]: "Montenegrin",
  [LanguageEnum.FAROESE]: "Faroese",
  [LanguageEnum.BOSNIAN]: "Bosnian",
  [LanguageEnum.PALAUAN]: "Palauan",
  [LanguageEnum.MARSHALLESE]: "Marshallese",
  [LanguageEnum.GILBERTESE]: "Gilbertese",
  [LanguageEnum.NAURU]: "Nauru",
  [LanguageEnum.BISLAMA]: "Bislama",
  [LanguageEnum.FIJIAN]: "Fijian",
  [LanguageEnum.TUVALUAN]: "Tuvaluan",
  [LanguageEnum.SAMOAN]: "Samoan",
  [LanguageEnum.TONGAN]: "Tongan",
  [LanguageEnum.RAROTONGAN]: "Rarotongan",
  [LanguageEnum.NIUEAN]: "Niuean",
  [LanguageEnum.TOKELAUAN]: "Tokelauan",
  [LanguageEnum.GREENLANDIC]: "Greenlandic",
};

const languageLabelZh: { [key in LanguageEnum]: string } = {
  [LanguageEnum.CHINESE]: "中文",
  [LanguageEnum.ENGLISH]: "英语",
  [LanguageEnum.HINDI]: "印地语",
  [LanguageEnum.SPANISH]: "西班牙语",
  [LanguageEnum.ARABIC]: "阿拉伯语",
  [LanguageEnum.FRENCH]: "法语",
  [LanguageEnum.BENGALI]: "孟加拉语",
  [LanguageEnum.RUSSIAN]: "俄语",
  [LanguageEnum.PORTUGUESE]: "葡萄牙语",
  [LanguageEnum.URDU]: "乌尔都语",
  [LanguageEnum.INDONESIAN]: "印度尼西亚语",
  [LanguageEnum.GERMAN]: "德语",
  [LanguageEnum.JAPANESE]: "日语",
  [LanguageEnum.VIETNAMESE]: "越南语",
  [LanguageEnum.KOREAN]: "韩语",
  [LanguageEnum.TAMIL]: "泰米尔语",
  [LanguageEnum.TURKISH]: "土耳其语",
  [LanguageEnum.ITALIAN]: "意大利语",
  [LanguageEnum.THAI]: "泰语",
  [LanguageEnum.PERSIAN]: "波斯语",
  [LanguageEnum.POLISH]: "波兰语",
  [LanguageEnum.UKRAINIAN]: "乌克兰语",
  [LanguageEnum.BURMESE]: "缅甸语",
  [LanguageEnum.SWAHILI]: "斯瓦希里语",
  [LanguageEnum.PASHTO]: "普什图语",
  [LanguageEnum.AMHARIC]: "阿姆哈拉语",
  [LanguageEnum.ROMANIAN]: "罗马尼亚语",
  [LanguageEnum.DUTCH]: "荷兰语",
  [LanguageEnum.SINHALA]: "僧伽罗语",
  [LanguageEnum.SWEDISH]: "瑞典语",
  [LanguageEnum.CZECH]: "捷克语",
  [LanguageEnum.HUNGARIAN]: "匈牙利语",
  [LanguageEnum.AZERBAIJANI]: "阿塞拜疆语",
  [LanguageEnum.GREEK]: "希腊语",
  [LanguageEnum.HEBREW]: "希伯来语",
  [LanguageEnum.NEPALI]: "尼泊尔语",
  [LanguageEnum.KHMER]: "高棉语",
  [LanguageEnum.ZULU]: "祖鲁语",
  [LanguageEnum.SOMALI]: "索马里语",
  [LanguageEnum.FINNISH]: "芬兰语",
  [LanguageEnum.BULGARIAN]: "保加利亚语",
  [LanguageEnum.SLOVAK]: "斯洛伐克语",
  [LanguageEnum.DANISH]: "丹麦语",
  [LanguageEnum.CATALAN]: "加泰罗尼亚语",
  [LanguageEnum.CROATIAN]: "克罗地亚语",
  [LanguageEnum.DZONGKHA]: "宗卡语",
  [LanguageEnum.ESTONIAN]: "爱沙尼亚语",
  [LanguageEnum.GEORGIAN]: "格鲁吉亚语",
  [LanguageEnum.ICELANDIC]: "冰岛语",
  [LanguageEnum.IRISH]: "爱尔兰语",
  [LanguageEnum.KAZAKH]: "哈萨克语",
  [LanguageEnum.KURDISH]: "库尔德语",
  [LanguageEnum.KYRGYZ]: "吉尔吉斯语",
  [LanguageEnum.LAO]: "老挝语",
  [LanguageEnum.LATIN]: "拉丁语",
  [LanguageEnum.LATVIAN]: "拉脱维亚语",
  [LanguageEnum.LITHUANIAN]: "立陶宛语",
  [LanguageEnum.MACEDONIAN]: "马其顿语",
  [LanguageEnum.MALAGASY]: "马尔加什语",
  [LanguageEnum.MALAY]: "马来语",
  [LanguageEnum.MALTESE]: "马耳他语",
  [LanguageEnum.MONGOLIAN]: "蒙古语",
  [LanguageEnum.NORWEGIAN]: "挪威语",
  [LanguageEnum.SERBIAN]: "塞尔维亚语",
  [LanguageEnum.SLOVENIAN]: "斯洛文尼亚语",
  [LanguageEnum.TAJIK]: "塔吉克语",
  [LanguageEnum.TURKMEN]: "土库曼语",
  [LanguageEnum.UZBEK]: "乌兹别克语",
  [LanguageEnum.XHOSA]: "科萨语",
  [LanguageEnum.FILIPINO]: "菲律宾语",
  [LanguageEnum.DIVEHI]: "迪维希语",
  [LanguageEnum.BELARUSIAN]: "白俄罗斯语",
  [LanguageEnum.ARMENIAN]: "亚美尼亚语",
  [LanguageEnum.TIGRINYA]: "提格里尼亚语",
  [LanguageEnum.KINYARWANDA]: "卢旺达语",
  [LanguageEnum.KIRUNDI]: "基隆迪语",
  [LanguageEnum.CAPE_VERDEAN_CREOLE]: "佛得角克里奥尔语",
  [LanguageEnum.NYANJA]: "尼扬贾语",
  [LanguageEnum.SWATI]: "斯瓦蒂语",
  [LanguageEnum.COMORIAN]: "科摩罗语",
  [LanguageEnum.SOUTHERN_SOTHO]: "南索托语",
  [LanguageEnum.HAITIAN_CREOLE]: "海地克里奥尔语",
  [LanguageEnum.LUXEMBOURGISH]: "卢森堡语",
  [LanguageEnum.ALBANIAN]: "阿尔巴尼亚语",
  [LanguageEnum.MONTENEGRIN]: "黑山语",
  [LanguageEnum.FAROESE]: "法罗语",
  [LanguageEnum.BOSNIAN]: "波斯尼亚语",
  [LanguageEnum.PALAUAN]: "帕劳语",
  [LanguageEnum.MARSHALLESE]: "马绍尔语",
  [LanguageEnum.GILBERTESE]: "吉尔伯特语",
  [LanguageEnum.NAURU]: "瑙鲁语",
  [LanguageEnum.BISLAMA]: "比斯拉马语",
  [LanguageEnum.FIJIAN]: "斐济语",
  [LanguageEnum.TUVALUAN]: "图瓦卢语",
  [LanguageEnum.SAMOAN]: "萨摩亚语",
  [LanguageEnum.TONGAN]: "汤加语",
  [LanguageEnum.RAROTONGAN]: "拉罗汤加语",
  [LanguageEnum.NIUEAN]: "纽埃语",
  [LanguageEnum.TOKELAUAN]: "托克劳语",
  [LanguageEnum.GREENLANDIC]: "格陵兰语",
};

const isChineseLanguage = (language?: string): boolean =>
  String(language || "").toLowerCase().startsWith("zh");

export const getLanguageLabel = (
  value: LanguageEnum,
  currentLanguage?: string,
): string =>
  isChineseLanguage(currentLanguage)
    ? languageLabelZh[value] || languageLabel[value] || value
    : languageLabel[value] || value;

export const getSelectLanguagesOptions = (currentLanguage?: string) =>
  Object.entries(LanguageEnum)
    .map(([, value]) => {
      return {
        label: getLanguageLabel(value as LanguageEnum, currentLanguage),
        value,
      };
    })
    ?.sort((a: { label: string }, b: { label: string }) =>
      a.label.localeCompare(b.label),
    );

export const selectLanguagesOptions = getSelectLanguagesOptions();

const wrapContentWithTooltip = (
  label: React.ReactNode,
  placementKey: string | undefined,
  t: any,
): React.ReactNode => {
  const title = placementKey
    ? getPlacementTooltipMap(placementKey, t)
    : undefined;
  return <Tooltip title={title}>{label}</Tooltip>;
};

const CONTENT_TYPE_LABEL: Record<
  string,
  { full: string; short: { en: string; zh: string } }
> = {
  youtubeDedicated: {
    full: "YouTube Dedicated video",
    short: { en: "Dedicated video", zh: "专题视频" },
  },
  youtubeIntegrated: {
    full: "YouTube Integrated video",
    short: { en: "Integrated video", zh: "插片视频" },
  },
  tiktokVideo: {
    full: "TikTok video",
    short: { en: "Video", zh: "视频" },
  },
  instagramReel: {
    full: "Instagram Reel",
    short: { en: "Reel", zh: "Reels" },
  },
  instagramCarouselPost: {
    full: "Instagram carousel post",
    short: { en: "Carousel post", zh: "轮播贴" },
  },
  twitterPost: {
    full: "X post",
    short: { en: "Post", zh: "帖子" },
  },
  linkedinPost: {
    full: "LinkedIn post",
    short: { en: "Post", zh: "帖子" },
  },
  linkedinImagePost: {
    full: "LinkedIn image post",
    short: { en: "Image post", zh: "图片帖子" },
  },
  linkedinVideoPost: {
    full: "LinkedIn video post",
    short: { en: "Video post", zh: "视频帖子" },
  },
};

const resolveContentTypeLabel = (
  labelKey: keyof typeof CONTENT_TYPE_LABEL,
  t: any | undefined,
  hidePlatform: boolean,
): string => {
  const item = CONTENT_TYPE_LABEL[labelKey];
  if (!item) return t ? t("common.video", "Video") : "Video";
  if (hidePlatform) {
    if (!t) return item.short.en;
    return isChineseLanguage(i18n.language) ? item.short.zh : item.short.en;
  }
  return t
    ? t(`campaignCreate.platformContentLabel.${labelKey}`)
    : item.full;
};

export const getContentType = (
  orderDetail: any,
  t?: any,
  hidePlatform = false,
): React.ReactNode => {
  const contentType: string =
    orderDetail?.interestedPriceFields?.find((el: any) => el.contentType)
      ?.contentType || orderDetail?.contentType;
  const platform: string = orderDetail?.platform;
  const orLabel = t ? t("campaignCreate.platformContentLabel.or") : " or ";

  if (platform === PlatFormEnum.YOUTUBE) {
    if (contentType === "Dedicated video") {
      const label = resolveContentTypeLabel("youtubeDedicated", t, hidePlatform);
      return wrapContentWithTooltip(
        label,
        PlatformContentEnum.YOUTUBE_DEDICATED,
        t,
      );
    }
    if (contentType === "Integrated video") {
      const label = resolveContentTypeLabel("youtubeIntegrated", t, hidePlatform);
      return wrapContentWithTooltip(
        label,
        PlatformContentEnum.YOUTUBE_INTEGRATED,
        t,
      );
    }
    const content: React.ReactNode[] = [];
    if (orderDetail?.availableAhaPicksOfferPlacements?.includes(PlatformContentEnum.YOUTUBE_DEDICATED)) {
      content.push(
        wrapContentWithTooltip(
          resolveContentTypeLabel(
            "youtubeDedicated",
            t,
            hidePlatform || content.length > 0,
          ),
          PlatformContentEnum.YOUTUBE_DEDICATED,
          t,
        ),
      );
    }
    if (orderDetail?.availableAhaPicksOfferPlacements?.includes(PlatformContentEnum.YOUTUBE_INTEGRATED)) {
      content.push(
        wrapContentWithTooltip(
          resolveContentTypeLabel(
            "youtubeIntegrated",
            t,
            hidePlatform || content.length > 0,
          ),
          PlatformContentEnum.YOUTUBE_INTEGRATED,
          t,
        ),
      );
    }
    if (content.length === 0) {
      const label = t ? t("common.video", "Video") : "Video";
      return wrapContentWithTooltip(label, undefined, t);
    }
    if (content.length === 1) return content[0];
    return (
      <>
        {content.map((node, i) => (
          <React.Fragment key={i}>
            {i > 0 ? orLabel : null}
            {node}
          </React.Fragment>
        ))}
      </>
    );
  }

  if (platform === PlatFormEnum.TIKTOK) {
    const label = resolveContentTypeLabel("tiktokVideo", t, hidePlatform);
    return wrapContentWithTooltip(label, PlatformContentEnum.TIKTOK_VIDEO, t);
  }

  if (platform === PlatFormEnum.INSTAGRAM) {
    if (contentType === "Reels") {
      const label = resolveContentTypeLabel("instagramReel", t, hidePlatform);
      return wrapContentWithTooltip(
        label,
        PlatformContentEnum.INSTAGRAM_REEL,
        t,
      );
    }
    if (contentType === "Post") {
      const label = resolveContentTypeLabel(
        "instagramCarouselPost",
        t,
        hidePlatform,
      );
      return wrapContentWithTooltip(
        label,
        PlatformContentEnum.INSTAGRAM_CAROUSEL_POST,
        t,
      );
    }
    const content: React.ReactNode[] = [];
    if (orderDetail?.availableAhaPicksOfferPlacements?.includes(PlatformContentEnum.INSTAGRAM_REEL)) {
      content.push(
        wrapContentWithTooltip(
          resolveContentTypeLabel(
            "instagramReel",
            t,
            hidePlatform || content.length > 0,
          ),
          PlatformContentEnum.INSTAGRAM_REEL,
          t,
        ),
      );
    }
    if (
      orderDetail?.availableAhaPicksOfferPlacements?.includes(PlatformContentEnum.INSTAGRAM_CAROUSEL_POST)
    ) {
      content.push(
        wrapContentWithTooltip(
          resolveContentTypeLabel(
            "instagramCarouselPost",
            t,
            hidePlatform || content.length > 0,
          ),
          PlatformContentEnum.INSTAGRAM_CAROUSEL_POST,
          t,
        ),
      );
    }
    if (content.length === 0) {
      const label = t ? t("common.video", "Video") : "Video";
      return wrapContentWithTooltip(label, undefined, t);
    }
    if (content.length === 1) return content[0];
    return (
      <>
        {content.map((node, i) => (
          <React.Fragment key={i}>
            {i > 0 ? orLabel : null}
            {node}
          </React.Fragment>
        ))}
      </>
    );
  }

  if (platform === PlatFormEnum.TWITTER || platform === PlatFormEnum.X) {
    const label = resolveContentTypeLabel("twitterPost", t, hidePlatform);
    return wrapContentWithTooltip(label, PlatformContentEnum.TWITTER_POST, t);
  }

  if (platform === PlatFormEnum.LINKEDIN) {
    if (contentType?.includes("Image")) {
      const label = resolveContentTypeLabel("linkedinImagePost", t, hidePlatform);
      return wrapContentWithTooltip(
        label,
        PlatformContentEnum.LINKEDIN_IMAGE_POST,
        t,
      );
    }
    if (contentType?.includes("Video")) {
      const label = resolveContentTypeLabel("linkedinVideoPost", t, hidePlatform);
      return wrapContentWithTooltip(
        label,
        PlatformContentEnum.LINKEDIN_VIDEO_POST,
        t,
      );
    }
    if (contentType?.includes("Post")) {
      const label = resolveContentTypeLabel("linkedinPost", t, hidePlatform);
      return wrapContentWithTooltip(
        label,
        PlatformContentEnum.LINKEDIN_POST,
        t,
      );
    }
    const content: React.ReactNode[] = [];
    if (
      orderDetail?.availableAhaPicksOfferPlacements?.includes(PlatformContentEnum.LINKEDIN_IMAGE_POST)
    ) {
      content.push(
        wrapContentWithTooltip(
          resolveContentTypeLabel(
            "linkedinImagePost",
            t,
            hidePlatform || content.length > 0,
          ),
          PlatformContentEnum.LINKEDIN_IMAGE_POST,
          t,
        ),
      );
    }
    if (
      orderDetail?.availableAhaPicksOfferPlacements?.includes(PlatformContentEnum.LINKEDIN_VIDEO_POST)
    ) {
      content.push(
        wrapContentWithTooltip(
          resolveContentTypeLabel(
            "linkedinVideoPost",
            t,
            hidePlatform || content.length > 0,
          ),
          PlatformContentEnum.LINKEDIN_VIDEO_POST,
          t,
        ),
      );
    }
    if (content.length === 0) {
      const label = resolveContentTypeLabel("linkedinPost", t, hidePlatform);
      return wrapContentWithTooltip(label, undefined, t);
    }
    if (content.length === 1) return content[0];
    return (
      <>
        {content.map((node, i) => (
          <React.Fragment key={`linkedin-content-${i}`}>
            {i > 0 ? orLabel : null}
            {node}
          </React.Fragment>
        ))}
      </>
    );
  }

  const label = t ? t("common.video", "Video") : "Video";
  return wrapContentWithTooltip(label, undefined, t);
};

// 达人黑名单模版
export const SAMPLE_INFLUENCER_LIST_CSV_LINK =
  "https://aha-assets-public.s3.amazonaws.com/influencer-list-template.csv";

// post date enum
export enum PostTimeType {
  FLEXIBLE = "flexible",
  FIXED = "fixed",
}

// campaign.customerScaleType 代表客户类型， LARGE_SCALE 是大客户
export enum CustomerScaleTypeList {
  SMALL_SCALE = "small_scale",
  LARGE_SCALE = "large_scale",
}

export const isTwitterOrLinkedInOnly = (platforms: string[]) => {
  const twitterLinkedInPlatforms = [
    PlatformContentEnum.TWITTER_POST,
    PlatformContentEnum.LINKEDIN_POST,
    PlatformContentEnum.LINKEDIN_IMAGE_POST,
    PlatformContentEnum.LINKEDIN_VIDEO_POST,
  ];
  return (
    platforms.length > 0 &&
    platforms.every((platform) =>
      twitterLinkedInPlatforms?.includes(platform as PlatformContentEnum),
    )
  );
};

export enum CampaignMarketingStrategy {
  INFLUENCER_MARKETING = 'influencer_marketing',
  AFFILIATES_MARKETING = 'affiliates_marketing',
  SALES_OUTREACH = 'sales_outreach',
}


enum ReginEnum {
  NORTH_AMERICA = "North America",
  LATIN_AMERICA = "Latin America",
  WESTERN_EUROPE = "Western Europe",
  EASTERN_EUROPE = "Eastern Europe",
  EASTERN_EUROPE_AND_RUSSIA = "Eastern Europe & Russia",
  MIDDLE_EAST = "Middle East",
  SOUTH_ASIA = "South Asia",
  SOUTH_EAST_ASIA = "Southeast Asia",
  EAST_ASIA = "East Asia",
  OCEANIA = "Oceania",
  AFRICA = "Africa",
  Other = "Other",
}

// 英语国家语言包
export const enLocationsBag = [
  [ReginEnum.NORTH_AMERICA, "US"],
  [ReginEnum.NORTH_AMERICA, "CA"],
  [ReginEnum.WESTERN_EUROPE, "GB"],
  [ReginEnum.WESTERN_EUROPE, "IE"],
  [ReginEnum.OCEANIA, "AU"],
  [ReginEnum.OCEANIA, "NZ"],
  [ReginEnum.SOUTH_EAST_ASIA, "SG"],
  [ReginEnum.EAST_ASIA, "HK"],
];

// 发达国家语言包
export const developedCountriesBag = [
  [ReginEnum.NORTH_AMERICA, "US"],
  [ReginEnum.EAST_ASIA, "JP"],
  [ReginEnum.WESTERN_EUROPE, "DE"],
  [ReginEnum.WESTERN_EUROPE, "GB"],
  [ReginEnum.WESTERN_EUROPE, "FR"],
  [ReginEnum.NORTH_AMERICA, "CA"],
  [ReginEnum.EAST_ASIA, "KR"],
  [ReginEnum.WESTERN_EUROPE, "IT"],
  [ReginEnum.OCEANIA, "AU"],
  [ReginEnum.WESTERN_EUROPE, "ES"],
  [ReginEnum.WESTERN_EUROPE, "NL"],
  [ReginEnum.WESTERN_EUROPE, "CH"],
  [ReginEnum.EAST_ASIA, "TW"],
  [ReginEnum.WESTERN_EUROPE, "SE"],
  [ReginEnum.WESTERN_EUROPE, "BE"],
  [ReginEnum.WESTERN_EUROPE, "AT"],
  [ReginEnum.WESTERN_EUROPE, "NO"],
  [ReginEnum.WESTERN_EUROPE, "IE"],
  [ReginEnum.SOUTH_EAST_ASIA, "SG"],
  [ReginEnum.EAST_ASIA, "HK"],
  [ReginEnum.WESTERN_EUROPE, "DK"],
  [ReginEnum.WESTERN_EUROPE, "FI"],
  [ReginEnum.OCEANIA, "NZ"],
  [ReginEnum.WESTERN_EUROPE, "GR"],
  [ReginEnum.WESTERN_EUROPE, "PT"],
  [ReginEnum.WESTERN_EUROPE, "AD"],
  [ReginEnum.WESTERN_EUROPE, "PT-30"],
  [ReginEnum.WESTERN_EUROPE, "ES-CN"],
  [ReginEnum.WESTERN_EUROPE, "HR"],
  [ReginEnum.MIDDLE_EAST, "CY"],
  [ReginEnum.WESTERN_EUROPE, "GI"],
  [ReginEnum.WESTERN_EUROPE, "IS"],
  [ReginEnum.WESTERN_EUROPE, "JE"],
  [ReginEnum.WESTERN_EUROPE, "LI"],
  [ReginEnum.WESTERN_EUROPE, "LU"],
  [ReginEnum.EAST_ASIA, "MO"],
  [ReginEnum.WESTERN_EUROPE, "MT"],
  [ReginEnum.WESTERN_EUROPE, "MC"],
  [ReginEnum.WESTERN_EUROPE, "SM"],
  [ReginEnum.WESTERN_EUROPE, "SI"],
  [ReginEnum.WESTERN_EUROPE, "VA"],
];

// 发达国家 + 高收入国家语言包
export const developedCountriesAndHighIncomeCountriesBag = [
  ...developedCountriesBag,
  [ReginEnum.LATIN_AMERICA, "BR"],
  [ReginEnum.LATIN_AMERICA, "MX"],
  [ReginEnum.SOUTH_EAST_ASIA, "ID"],
  [ReginEnum.MIDDLE_EAST, "SA"],
  [ReginEnum.MIDDLE_EAST, "TR"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "PL"],
  [ReginEnum.LATIN_AMERICA, "AR"],
  [ReginEnum.SOUTH_EAST_ASIA, "TH"],
  [ReginEnum.MIDDLE_EAST, "AE"],
  [ReginEnum.SOUTH_EAST_ASIA, "MY"],
  [ReginEnum.SOUTH_EAST_ASIA, "PH"],
  [ReginEnum.LATIN_AMERICA, "CO"],
  [ReginEnum.SOUTH_EAST_ASIA, "VN"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "CZ"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "RO"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "HU"],
  [ReginEnum.MIDDLE_EAST, "IL"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "AL"],
  [ReginEnum.OCEANIA, "AS"],
  [ReginEnum.NORTH_AMERICA, "AI"],
  [ReginEnum.LATIN_AMERICA, "AG"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "AM"],
  [ReginEnum.LATIN_AMERICA, "AW"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "AZ"],
  [ReginEnum.LATIN_AMERICA, "BS"],
  [ReginEnum.MIDDLE_EAST, "BH"],
  [ReginEnum.LATIN_AMERICA, "BB"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "BY"],
  [ReginEnum.LATIN_AMERICA, "BZ"],
  [ReginEnum.NORTH_AMERICA, "BM"],
  [ReginEnum.LATIN_AMERICA, "BO"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "BA"],
  [ReginEnum.Other, "VG"],
  [ReginEnum.SOUTH_EAST_ASIA, "BN"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "BG"],
  [ReginEnum.SOUTH_EAST_ASIA, "KH"],
  [ReginEnum.NORTH_AMERICA, "KY"],
  [ReginEnum.LATIN_AMERICA, "CL"],
  [ReginEnum.OCEANIA, "CK"],
  [ReginEnum.LATIN_AMERICA, "CR"],
  [ReginEnum.LATIN_AMERICA, "CU"],
  [ReginEnum.LATIN_AMERICA, "CW"],
  [ReginEnum.LATIN_AMERICA, "DM"],
  [ReginEnum.LATIN_AMERICA, "DO"],
  [ReginEnum.LATIN_AMERICA, "EC"],
  [ReginEnum.MIDDLE_EAST, "EG"],
  [ReginEnum.LATIN_AMERICA, "SV"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "EE"],
  [ReginEnum.Other, "FO"],
  [ReginEnum.OCEANIA, "FJ"],
  [ReginEnum.OCEANIA, "PF"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "GE"],
  [ReginEnum.NORTH_AMERICA, "GL"],
  [ReginEnum.LATIN_AMERICA, "GD"],
  [ReginEnum.LATIN_AMERICA, "GP"],
  [ReginEnum.OCEANIA, "GU"],
  [ReginEnum.LATIN_AMERICA, "GT"],
  [ReginEnum.Other, "GG"],
  [ReginEnum.LATIN_AMERICA, "GY"],
  [ReginEnum.LATIN_AMERICA, "HT"],
  [ReginEnum.LATIN_AMERICA, "HN"],
  [ReginEnum.MIDDLE_EAST, "IR"],
  [ReginEnum.MIDDLE_EAST, "IQ"],
  [ReginEnum.Other, "IM"],
  [ReginEnum.LATIN_AMERICA, "JM"],
  [ReginEnum.MIDDLE_EAST, "JO"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "KZ"],
  [ReginEnum.OCEANIA, "KI"],
  [ReginEnum.MIDDLE_EAST, "KW"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "KG"],
  [ReginEnum.SOUTH_EAST_ASIA, "LA"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "LV"],
  [ReginEnum.MIDDLE_EAST, "LB"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "LT"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "MK"],
  [ReginEnum.OCEANIA, "MH"],
  [ReginEnum.LATIN_AMERICA, "MQ"],
  [ReginEnum.OCEANIA, "FM"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "MD"],
  [ReginEnum.EAST_ASIA, "MN"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "ME"],
  [ReginEnum.NORTH_AMERICA, "MS"],
  [ReginEnum.SOUTH_EAST_ASIA, "MM"],
  [ReginEnum.OCEANIA, "NR"],
  [ReginEnum.OCEANIA, "NC"],
  [ReginEnum.LATIN_AMERICA, "NI"],
  [ReginEnum.OCEANIA, "NU"],
  [ReginEnum.OCEANIA, "MP"],
  [ReginEnum.MIDDLE_EAST, "OM"],
  [ReginEnum.OCEANIA, "PW"],
  [ReginEnum.MIDDLE_EAST, "PS"],
  [ReginEnum.LATIN_AMERICA, "PA"],
  [ReginEnum.OCEANIA, "PG"],
  [ReginEnum.LATIN_AMERICA, "PY"],
  [ReginEnum.LATIN_AMERICA, "PE"],
  [ReginEnum.Other, "PN"],
  [ReginEnum.Other, "PR"],
  [ReginEnum.MIDDLE_EAST, "QA"],
  [ReginEnum.Other, "RE"],
  [ReginEnum.Other, "MF"],
  [ReginEnum.OCEANIA, "WS"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "RS"],
  [ReginEnum.NORTH_AMERICA, "SX"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "SK"],
  [ReginEnum.OCEANIA, "SB"],
  [ReginEnum.Other, "SH"],
  [ReginEnum.LATIN_AMERICA, "KN"],
  [ReginEnum.LATIN_AMERICA, "LC"],
  [ReginEnum.Other, "PM"],
  [ReginEnum.LATIN_AMERICA, "VC"],
  [ReginEnum.MIDDLE_EAST, "SY"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "TJ"],
  [ReginEnum.SOUTH_EAST_ASIA, "TL"],
  [ReginEnum.Other, "TK"],
  [ReginEnum.OCEANIA, "TO"],
  [ReginEnum.LATIN_AMERICA, "TT"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "TM"],
  [ReginEnum.Other, "TC"],
  [ReginEnum.OCEANIA, "TV"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "UA"],
  [ReginEnum.LATIN_AMERICA, "UY"],
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA, "UZ"],
  [ReginEnum.OCEANIA, "VU"],
  [ReginEnum.LATIN_AMERICA, "VE"],
  [ReginEnum.OCEANIA, "WF"],
  [ReginEnum.MIDDLE_EAST, "YE"],
];

export const locationOptions = [
  {
    label: ReginEnum.NORTH_AMERICA,
    value: ReginEnum.NORTH_AMERICA,
    children: [
      {
        label: "Canada",
        value: "CA",
      },
      {
        label: "United States",
        value: "US",
      },
      {
        label: "Greenland",
        value: "GL",
      },
      {
        label: "Anguilla",
        value: "AI",
      },
      {
        label: "Montserrat",
        value: "MS",
      },
      {
        label: "Sint Maarten",
        value: "SX",
      },
      {
        label: "Cayman Islands",
        value: "KY",
      },
      {
        label: "Bermuda",
        value: "BM",
      },
    ],
  },
  {
    label: ReginEnum.LATIN_AMERICA,
    value: ReginEnum.LATIN_AMERICA,
    children: [
      {
        label: "Mexico",
        value: "MX",
      },
      {
        label: "Belize",
        value: "BZ",
      },
      {
        label: "Costa Rica",
        value: "CR",
      },
      {
        label: "El Salvador",
        value: "SV",
      },
      {
        label: "Guatemala",
        value: "GT",
      },
      {
        label: "Honduras",
        value: "HN",
      },
      {
        label: "Nicaragua",
        value: "NI",
      },
      {
        label: "Panama",
        value: "PA",
      },
      {
        label: "Argentina",
        value: "AR",
      },
      {
        label: "Bolivia",
        value: "BO",
      },
      {
        label: "Brazil",
        value: "BR",
      },
      {
        label: "Chile",
        value: "CL",
      },
      {
        label: "Colombia",
        value: "CO",
      },
      {
        label: "Ecuador",
        value: "EC",
      },
      {
        label: "Guyana",
        value: "GY",
      },
      {
        label: "Paraguay",
        value: "PY",
      },
      {
        label: "Peru",
        value: "PE",
      },
      {
        label: "Suriname",
        value: "SR",
      },
      {
        label: "Uruguay",
        value: "UY",
      },
      {
        label: "Venezuela",
        value: "VE",
      },
      {
        label: "Antigua & Barbuda",
        value: "AG",
      },
      {
        label: "Bahamas",
        value: "BS",
      },
      {
        label: "Barbados",
        value: "BB",
      },
      {
        label: "Cuba",
        value: "CU",
      },
      {
        label: "Dominica",
        value: "DM",
      },
      {
        label: "Dominican Republic",
        value: "DO",
      },
      {
        label: "Grenada",
        value: "GD",
      },
      {
        label: "Haiti",
        value: "HT",
      },
      {
        label: "Jamaica",
        value: "JM",
      },
      {
        label: "St. Kitts & Nevis",
        value: "KN",
      },
      {
        label: "St. Lucia",
        value: "LC",
      },
      {
        label: "St. Vincent & Grenadines",
        value: "VC",
      },
      {
        label: "Trinidad & Tobago",
        value: "TT",
      },
      {
        label: "Aruba",
        value: "AW",
      },
      {
        label: "CuraÇAo",
        value: "CW",
      },
      {
        label: "Caribbean Netherlands",
        value: "BQ",
      },
      {
        label: "Guadeloupe",
        value: "GP",
      },
      {
        label: "Martinique",
        value: "MQ",
      },
    ],
  },
  {
    label: ReginEnum.WESTERN_EUROPE,
    value: ReginEnum.WESTERN_EUROPE,
    children: [
      {
        label: "Ireland",
        value: "IE",
      },
      {
        label: "United Kingdom",
        value: "GB",
      },
      {
        label: "France",
        value: "FR",
      },
      {
        label: "Germany",
        value: "DE",
      },
      {
        label: "Spain",
        value: "ES",
      },
      {
        label: "Portugal",
        value: "PT",
      },
      {
        label: "Belgium",
        value: "BE",
      },
      {
        label: "Netherlands",
        value: "NL",
      },
      {
        label: "Luxembourg",
        value: "LU",
      },
      {
        label: "Switzerland",
        value: "CH",
      },
      {
        label: "Austria",
        value: "AT",
      },
      {
        label: "Italy",
        value: "IT",
      },
      {
        label: "Monaco",
        value: "MC",
      },
      {
        label: "Liechtenstein",
        value: "LI",
      },
      {
        label: "San Marino",
        value: "SM",
      },
      {
        label: "Vatican City",
        value: "VA",
      },
      {
        label: "Norway",
        value: "NO",
      },
      {
        label: "Iceland",
        value: "IS",
      },
      {
        label: "Denmark",
        value: "DK",
      },
      {
        label: "Finland",
        value: "FI",
      },
      {
        label: "Greece",
        value: "GR",
      },
      {
        label: "Croatia",
        value: "HR",
      },
      {
        label: "Slovenia",
        value: "SI",
      },
      {
        label: "Sweden",
        value: "SE",
      },
      {
        label: "Madeira",
        value: "#N/A",
      },
      {
        label: "Malta",
        value: "MT",
      },
      {
        label: "Andorra",
        value: "AD",
      },
      {
        label: "Gibraltar",
        value: "GI",
      },
      {
        label: "Jersey",
        value: "JE",
      },
      {
        label: "Azores",
        value: "PT-30",
      },
      {
        label: "Canary Islands",
        value: "ES-CN",
      },
    ],
  },
  {
    label: ReginEnum.EASTERN_EUROPE,
    value: ReginEnum.EASTERN_EUROPE_AND_RUSSIA,
    children: [
      {
        label: "Belarus",
        value: "BY",
      },
      {
        label: "Ukraine",
        value: "UA",
      },
      {
        label: "Poland",
        value: "PL",
      },
      {
        label: "Czechia",
        value: "CZ",
      },
      {
        label: "Slovakia",
        value: "SK",
      },
      {
        label: "Hungary",
        value: "HU",
      },
      {
        label: "Romania",
        value: "RO",
      },
      {
        label: "Bulgaria",
        value: "BG",
      },
      {
        label: "Lithuania",
        value: "LT",
      },
      {
        label: "Latvia",
        value: "LV",
      },
      {
        label: "Estonia",
        value: "EE",
      },
      {
        label: "Moldova",
        value: "MD",
      },
      {
        label: "Serbia",
        value: "RS",
      },
      {
        label: "Bosnia and Herzegovina",
        value: "BA",
      },
      {
        label: "Montenegro",
        value: "ME",
      },
      {
        label: "Albania",
        value: "AL",
      },
      {
        label: "Macedonia",
        value: "MK",
      },
      {
        label: "Armenia",
        value: "AM",
      },
      {
        label: "Azerbaijan",
        value: "AZ",
      },
      {
        label: "Tajikistan",
        value: "TJ",
      },
      {
        label: "Kazakhstan",
        value: "KZ",
      },
      {
        label: "Kyrgyzstan",
        value: "KG",
      },
      {
        label: "Tajikistan",
        value: "UZ",
      },
      {
        label: "Turkmenistan",
        value: "TM",
      },
      {
        label: "Georgia",
        value: "GE",
      },
    ],
  },
  {
    label: ReginEnum.MIDDLE_EAST,
    value: ReginEnum.MIDDLE_EAST,
    children: [
      {
        label: "Saudi Arabia",
        value: "SA",
      },
      {
        label: "United Arab Emirates",
        value: "AE",
      },
      {
        label: "Israel",
        value: "IL",
      },
      {
        label: "Jordan",
        value: "JO",
      },
      {
        label: "Lebanon",
        value: "LB",
      },
      {
        label: "Syria",
        value: "SY",
      },
      {
        label: "Iraq",
        value: "IQ",
      },
      {
        label: "Kuwait",
        value: "KW",
      },
      {
        label: "Oman",
        value: "OM",
      },
      {
        label: "Qatar",
        value: "QA",
      },
      {
        label: "Bahrain",
        value: "BH",
      },
      {
        label: "Yemen",
        value: "YE",
      },
      {
        label: "Iran",
        value: "IR",
      },
      {
        label: "Türkiye",
        value: "TR",
      },
      {
        label: "Palestine",
        value: "PS",
      },
      {
        label: "Egypt",
        value: "EG",
      },
      {
        label: "Cyprus",
        value: "CY",
      },
    ],
  },
  {
    label: ReginEnum.SOUTH_ASIA,
    value: ReginEnum.SOUTH_ASIA,
    children: [
      {
        label: "India",
        value: "IN",
      },
      {
        label: "Pakistan",
        value: "PK",
      },
      {
        label: "Bangladesh",
        value: "BD",
      },
      {
        label: "Nepal",
        value: "NP",
      },
      {
        label: "Sri Lanka",
        value: "LK",
      },
      {
        label: "Bhutan",
        value: "BT",
      },
      {
        label: "Maldives",
        value: "MV",
      },
      {
        label: "Afghanistan",
        value: "AF",
      },
    ],
  },
  {
    label: ReginEnum.SOUTH_EAST_ASIA,
    value: ReginEnum.SOUTH_EAST_ASIA,
    children: [
      {
        label: "Myanmar",
        value: "MM",
      },
      {
        label: "Thailand",
        value: "TH",
      },
      {
        label: "Vietnam",
        value: "VN",
      },
      {
        label: "Malaysia",
        value: "MY",
      },
      {
        label: "Singapore",
        value: "SG",
      },
      {
        label: "Indonesia",
        value: "ID",
      },
      {
        label: "Philippines",
        value: "PH",
      },
      {
        label: "Cambodia",
        value: "KH",
      },
      {
        label: "Laos",
        value: "LA",
      },
      {
        label: "Timor-Leste",
        value: "TL",
      },
      {
        label: "Brunei",
        value: "BN",
      },
    ],
  },
  {
    label: ReginEnum.EAST_ASIA,
    value: ReginEnum.EAST_ASIA,
    children: [
      // {
      //   label: 'China',
      //   value: 'CN'
      // },
      {
        label: "Japan",
        value: "JP",
      },
      {
        label: "South Korea",
        value: "KR",
      },
      {
        label: "North Korea",
        value: "KP",
      },
      {
        label: "Mongolia",
        value: "MN",
      },
      {
        label: "Taiwan Province, China",
        value: "TW",
      },
      {
        label: "Hong Kong SAR China",
        value: "HK",
      },
      {
        label: "Macau SAR China",
        value: "MO",
      },
    ],
  },
  {
    label: ReginEnum.OCEANIA,
    value: ReginEnum.OCEANIA,
    children: [
      {
        label: "Australia",
        value: "AU",
      },
      {
        label: "New Zealand",
        value: "NZ",
      },
      {
        label: "Papua New Guinea",
        value: "PG",
      },
      {
        label: "Fiji",
        value: "FJ",
      },
      {
        label: "Solomon Islands",
        value: "SB",
      },
      {
        label: "Vanuatu",
        value: "VU",
      },
      {
        label: "Samoa",
        value: "WS",
      },
      {
        label: "Kiribati",
        value: "KI",
      },
      {
        label: "Tonga",
        value: "TO",
      },
      {
        label: "Micronesia",
        value: "FM",
      },
      {
        label: "Marshall Islands",
        value: "MH",
      },
      {
        label: "Palau",
        value: "PW",
      },
      {
        label: "Nauru",
        value: "NR",
      },
      {
        label: "Tuvalu",
        value: "TV",
      },
      {
        label: "New Caledonia",
        value: "NC",
      },
      {
        label: "French Polynesia",
        value: "PF",
      },
      {
        label: "Wallis & Futuna",
        value: "WF",
      },
      {
        label: "Cook Islands",
        value: "CK",
      },
      {
        label: "Niue",
        value: "NU",
      },
      {
        label: "American Samoa",
        value: "AS",
      },
      {
        label: "Northern Mariana Islands",
        value: "MP",
      },
      {
        label: "Guam",
        value: "GU",
      },
    ],
  },
  {
    label: ReginEnum.AFRICA,
    value: ReginEnum.AFRICA,
    children: [
      {
        label: "Algeria",
        value: "DZ",
      },
      {
        label: "Angola",
        value: "AO",
      },
      {
        label: "Benin",
        value: "BJ",
      },
      {
        label: "Botswana",
        value: "BW",
      },
      {
        label: "Burkina Faso",
        value: "BF",
      },
      {
        label: "Burundi",
        value: "BI",
      },
      {
        label: "Cameroon",
        value: "CM",
      },
      {
        label: "Central African Republic",
        value: "CF",
      },
      {
        label: "Chad",
        value: "TD",
      },
      {
        label: "Comoros",
        value: "KM",
      },
      {
        label: "Democratic Republic of the Congo",
        value: "CD",
      },
      {
        label: "Republic of the Congo",
        value: "CG",
      },
      {
        label: "Côte d'Ivoire",
        value: "CI",
      },
      {
        label: "Djibouti",
        value: "DJ",
      },
      {
        label: "Equatorial Guinea",
        value: "GQ",
      },
      {
        label: "Eritrea",
        value: "ER",
      },
      {
        label: "Swaziland",
        value: "SZ",
      },
      {
        label: "Ethiopia",
        value: "ET",
      },
      {
        label: "Gabon",
        value: "GA",
      },
      {
        label: "Gambia",
        value: "GM",
      },
      {
        label: "Ghana",
        value: "GH",
      },
      {
        label: "Guinea",
        value: "GN",
      },
      {
        label: "Guinea-Bissau",
        value: "GW",
      },
      {
        label: "Kenya",
        value: "KE",
      },
      {
        label: "Lesotho",
        value: "LS",
      },
      {
        label: "Liberia",
        value: "LR",
      },
      {
        label: "Madagascar",
        value: "MG",
      },
      {
        label: "Malawi",
        value: "MW",
      },
      {
        label: "Mali",
        value: "ML",
      },
      {
        label: "Mauritania",
        value: "MR",
      },
      {
        label: "Mauritius",
        value: "MU",
      },
      {
        label: "Mayotte",
        value: "YT",
      },
      {
        label: "Morocco",
        value: "MA",
      },
      {
        label: "Mozambique",
        value: "MZ",
      },
      {
        label: "Namibia",
        value: "NA",
      },
      {
        label: "Niger",
        value: "NE",
      },
      {
        label: "Nigeria",
        value: "NG",
      },
      {
        label: "Rwanda",
        value: "RW",
      },
      {
        label: "São Tomé and Príncipe",
        value: "ST",
      },
      {
        label: "Senegal",
        value: "SN",
      },
      {
        label: "Seychelles",
        value: "SC",
      },
      {
        label: "Sierra Leone",
        value: "SL",
      },
      {
        label: "Somalia",
        value: "SO",
      },
      {
        label: "South Africa",
        value: "ZA",
      },
      {
        label: "South Sudan",
        value: "SS",
      },
      {
        label: "Sudan",
        value: "SD",
      },
      {
        label: "Tanzania",
        value: "TZ",
      },
      {
        label: "Togo",
        value: "TG",
      },
      {
        label: "Uganda",
        value: "UG",
      },
      {
        label: "Western Sahara",
        value: "EH",
      },
      {
        label: "Zambia",
        value: "ZM",
      },
      {
        label: "Zimbabwe",
        value: "ZW",
      },
      {
        label: "Libya",
        value: "LY",
      },
      {
        label: "Tunisia",
        value: "TN",
      },
      {
        label: "Cape Verde",
        value: "CV",
      },
    ],
  },
  {
    label: ReginEnum.Other,
    value: ReginEnum.Other,
    children: [
      {
        label: "Antarctica",
        value: "AQ",
      },
      {
        label: "Åland Islands",
        value: "AX",
      },
      {
        label: "Bouvet Island",
        value: "BV",
      },
      {
        label: "British Indian Ocean Territory",
        value: "IO",
      },
      {
        label: "Falkland Islands",
        value: "FK",
      },
      {
        label: "Faroe Islands",
        value: "FO",
      },
      {
        label: "Guernsey",
        value: "GG",
      },
      {
        label: "Heard Island and McDonald Islands",
        value: "HM",
      },
      {
        label: "Isle of Man",
        value: "IM",
      },
      {
        label: "Jersey",
        value: "JE",
      },
      {
        label: "Saint Martin",
        value: "MF",
      },
      {
        label: "St. Pierre & Miquelon",
        value: "PM",
      },
      {
        label: "Pitcairn Islands",
        value: "PN",
      },
      {
        label: "Puerto Rico",
        value: "PR",
      },
      {
        label: "Réunion",
        value: "RE",
      },
      {
        label: "St. Helena",
        value: "SH",
      },
      {
        label: "Svalbard and Jan Mayen",
        value: "SJ",
      },
      {
        label: "Tokelau",
        value: "TK",
      },
      {
        label: "Turks & Caicos Islands",
        value: "TC",
      },
      {
        label: "U.S. Minor Outlying Islands",
        value: "UM",
      },
      {
        label: "British Virgin Islands",
        value: "VG",
      },
      {
        label: "U.S. Virgin Islands",
        value: "VI",
      },
    ],
  },
];

// 枚举的动态生成函数
function createEnum(
  data: {
    children: { label: string; value: string }[];
  }[],
): Record<string, string> {
  const enumObject: Record<string, string> = {};

  data?.forEach((group) => {
    group?.children?.forEach((item) => {
      enumObject[item.value] = item.label;
    });
  });
  return enumObject;
}

export const NationsLab: Record<string, string> = {
  ...createEnum(locationOptions),
  CN: "China",
};

const locationGroupLabelZh: Record<string, string> = {
  [ReginEnum.NORTH_AMERICA]: "北美",
  [ReginEnum.LATIN_AMERICA]: "拉丁美洲",
  [ReginEnum.WESTERN_EUROPE]: "西欧",
  [ReginEnum.EASTERN_EUROPE]: "东欧",
  [ReginEnum.EASTERN_EUROPE_AND_RUSSIA]: "东欧及俄罗斯",
  [ReginEnum.MIDDLE_EAST]: "中东",
  [ReginEnum.SOUTH_ASIA]: "南亚",
  [ReginEnum.SOUTH_EAST_ASIA]: "东南亚",
  [ReginEnum.EAST_ASIA]: "东亚",
  [ReginEnum.OCEANIA]: "大洋洲",
  [ReginEnum.AFRICA]: "非洲",
  [ReginEnum.Other]: "其他",
  developedEnglishSpeakingMarkets: "发达英语市场",
  developedMarkets: "发达市场",
  developedAndEmergingMarkets: "发达及新兴市场",
};

const countryLabelZhOverrides: Record<string, string> = {
  "#N/A": "马德拉群岛",
  "PT-30": "亚速尔群岛",
  "ES-CN": "加那利群岛",
  BQ: "荷兰加勒比区",
  CD: "刚果（金）",
  CG: "刚果（布）",
  CN: "中国",
  CW: "库拉索",
  HK: "中国香港特别行政区",
  MO: "中国澳门特别行政区",
  PS: "巴勒斯坦",
  SZ: "斯威士兰",
  TR: "土耳其",
  TW: "中国台湾",
};

const getIntlDisplayName = (
  locale: string,
  type: "region",
  code: string,
): string | undefined => {
  try {
    const DisplayNames = (Intl as any).DisplayNames;
    if (!DisplayNames) return undefined;
    return new DisplayNames([locale], { type }).of(code);
  } catch {
    return undefined;
  }
};

export const getCountryLabel = (
  value: string | undefined,
  currentLanguage?: string,
  fallback?: string,
): string => {
  const code = String(value || "");
  if (!code) return fallback || "";

  if (isChineseLanguage(currentLanguage)) {
    return (
      countryLabelZhOverrides[code] ||
      (/^[A-Z]{2}$/.test(code)
        ? getIntlDisplayName("zh-CN", "region", code)
        : undefined) ||
      fallback ||
      NationsLab[code] ||
      code
    );
  }

  return fallback || NationsLab[code] || code;
};

export const getLocationGroupLabel = (
  value: string | undefined,
  currentLanguage?: string,
  fallback?: string,
): string => {
  const groupValue = String(value || "");
  if (!groupValue) return fallback || "";

  if (isChineseLanguage(currentLanguage)) {
    return locationGroupLabelZh[groupValue] || fallback || groupValue;
  }

  return fallback || groupValue;
};

const languageToCountryMap: any = {
  id: "ID",
  ms: "MY,BN",
  vi: "VN",
  fil: "PH",
  th: "TH",
  lo: "LA",
  km: "KH",
  my: "MM",
  ko: "KR",
  ja: "JP",
  zh: "TW,HK,MO",
  mn: "MN",
  hi: "IN",
  ur: "PK",
  bn: "BD",
  ne: "NP",
  si: "LK",
  dv: "MV",
  dz: "BT",
  ru: "RU,KZ,KG,BY",
  uk: "UA",
  be: "BY",
  ro: "MD,RO",
  tg: "TJ",
  kk: "KZ",
  ky: "KG",
  uz: "UZ",
  tk: "TM",
  fa: "IR",
  tr: "TR",
  he: "IL",
  el: "CY,GR",
  ka: "GE",
  hy: "AM",
  az: "AZ",
  am: "ET",
  ti: "ER",
  so: "SO",
  sw: "TZ",
  rw: "RW",
  rn: "BI",
  crs: "SC",
  zu: "ZA",
  pt: "PT",
  ny: "ZW",
  mg: "MG",
  ss: "SZ",
  swb: "KM",
  st: "LS",
  ar: "MR",
  kl: "CA",
  ht: "CR",
  nl: "NL,BE",
  de: "DE,AT,CH,LI",
  it: "IT",
  sv: "SE",
  no: "NO",
  da: "DK",
  fi: "FI",
  is: "IS",
  ga: "IE",
  pl: "PL",
  cs: "CZ",
  sk: "SK",
  hu: "HU",
  lb: "LU",
  bg: "BG",
  sr: "RS",
  sq: "AL",
  sl: "SI",
  hr: "HR",
  cnr: "ME",
  mt: "MT",
  la: "VA",
  ca: "AD",
  et: "EE",
  lv: "LV",
  lt: "LT",
  fo: "FO",
  bs: "BA",
  pau: "PW",
  mh: "MH",
  gil: "KI",
  na: "NR",
  bi: "VU",
  fj: "FJ",
  tvl: "TV",
  sm: "WS,AS",
  to: "TO",
  rar: "CK",
  niu: "NU",
  tkl: "TK",
  mk: "MK",
};

const countryLanguageMap: any = {
  ID: "id",
  MY: "ms",
  VN: "vi",
  PH: "fil,en",
  TH: "th",
  SG: "en,ms,zh,ta",
  LA: "lo",
  KH: "km",
  MM: "my",
  BN: "ms",
  TL: "pt",
  KR: "ko",
  JP: "ja",
  TW: "zh",
  HK: "zh,en",
  MO: "zh,pt",
  MN: "mn",
  IN: "hi,en",
  PK: "ur,en",
  BD: "bn",
  NP: "ne",
  LK: "si,ta",
  MV: "dv",
  BT: "dz",
  RU: "ru",
  UA: "uk",
  BY: "be,ru",
  MD: "ro",
  TJ: "tg",
  KZ: "kk,ru",
  KG: "ky,ru",
  UZ: "uz",
  TM: "tk",
  SA: "ar",
  IR: "fa",
  IQ: "ar,ku",
  KW: "ar",
  AE: "ar",
  OM: "ar",
  QA: "ar",
  BH: "ar",
  TR: "tr",
  IL: "he",
  PS: "ar",
  SY: "ar",
  LB: "ar",
  JO: "ar",
  YE: "ar",
  CY: "el,tr",
  GE: "ka",
  AM: "hy",
  AZ: "az",
  EG: "ar",
  LY: "ar",
  TN: "ar",
  DZ: "ar",
  MA: "ar",
  EH: "ar",
  SD: "ar,en",
  SS: "en",
  ET: "am",
  ER: "ti,ar,en",
  SO: "so,ar",
  DJ: "fr,ar",
  KE: "en,sw",
  TZ: "sw,en",
  UG: "en,sw",
  RW: "rw,en,fr",
  BI: "rn,fr,en",
  SC: "crs,en,fr",
  ZA: "zu,xh,en",
  ZM: "en",
  AO: "pt",
  ZW: "ny,en",
  MU: "en,fr",
  MG: "mg,fr",
  SZ: "ss,en",
  KM: "swb,ar,fr",
  NA: "en",
  MW: "en",
  MZ: "pt",
  BW: "en",
  LS: "st,en",
  RE: "fr",
  SH: "en",
  NG: "en",
  NE: "fr",
  MR: "ar",
  SN: "en",
  GM: "fr",
  ML: "en",
  BF: "fr",
  GN: "pt",
  GW: "pt",
  CV: "en",
  SL: "en",
  LR: "fr",
  CI: "en",
  GH: "en",
  TG: "fr",
  BJ: "en",
  TD: "es",
  CM: "es",
  GQ: "es",
  GA: "en",
  ST: "en,fr",
  US: "es,en",
  CA: "en",
  MX: "fr",
  GL: "pt",
  PM: "es",
  BR: "pt",
  CO: "es",
  AR: "es",
  PE: "en",
  CL: "es",
  JM: "es",
  PA: "en",
  CU: "es",
  BS: "es",
  UY: "es",
  PY: "es",
  EC: "es",
  VE: "es",
  GT: "es",
  BO: "es",
  NI: "es",
  CR: "ht",
  HT: "es",
  DO: "en",
  AG: "en",
  KN: "en",
  DM: "en",
  LC: "en",
  VC: "en",
  GD: "en",
  BB: "en",
  TT: "es,en",
  PR: "en",
  VG: "en",
  AI: "en",
  MS: "fr",
  GP: "fr",
  MQ: "nl",
  AW: "nl,en",
  CW: "nl,en",
  SX: "en",
  TC: "en",
  KY: "en",
  BM: "fr",
  MF: "en",
  BZ: "en",
  SV: "es",
  HN: "es",
  FR: "fr",
  GB: "en",
  DE: "de",
  AT: "de",
  ES: "es",
  PT: "pt",
  IT: "it",
  GR: "el",
  SE: "sv",
  NO: "no",
  DK: "da",
  FI: "fi,sv",
  IS: "is",
  CH: "de,fr,it",
  IE: "ga,en",
  NL: "nl",
  PL: "pl",
  CZ: "cs",
  SK: "sk",
  HU: "hu",
  LI: "de",
  BE: "nl,fr,de",
  LU: "lb,fr,de",
  MC: "fr",
  RO: "ro",
  BG: "bg",
  RS: "sr",
  AL: "sq",
  SI: "sl",
  HR: "hr",
  ME: "cnr,sr",
  MT: "mt,en",
  VA: "la,it",
  AD: "ca",
  SM: "it",
  EE: "et",
  LV: "lv",
  LT: "lt",
  FO: "fo,da",
  GI: "en",
  BA: "bs,hr",
  AU: "en",
  NZ: "en",
  PW: "pau,en",
  FM: "en",
  MH: "mh,en",
  KI: "gil,en",
  NR: "na,en",
  MP: "en",
  GU: "en",
  PG: "en",
  SB: "en",
  VU: "bi,en",
  FJ: "fj,en",
  NC: "fr",
  TV: "tvl,en",
  WS: "sm,en",
  TO: "to,en",
  CK: "rar",
  NU: "niu,en",
  TK: "tkl,en",
  PF: "fr",
  WF: "fr",
  PN: "en",
  AS: "sm,en",
  YT: "fr",
  IM: "en",
  GG: "en,fr",
  GY: "en",
  MK: "mk",
  KP: "ko",
};

// 检查国家和语言的diff
export const checkDiffCToL = (languages: string[], locations: string[]) => {
  // 缺失
  const miss = {
    country: new Set(),
    languages: new Set(),
    // 语言简称
    languageAbbreviation: new Set(),
    // 国家简称
    countryAbbreviation: new Set(),
  };
  const selected = {
    country: new Set(),
    languages: new Set(),
  };
  const location = locations?.map((item) => item?.[1]);
  location?.forEach((country) => {
    const language = countryLanguageMap[country];
    if (language?.includes(",")) {
      const languageArr = language?.split(",");
      languageArr?.forEach((l: LanguageEnum) => {
        if (!languages?.includes(l) && languageLabel?.[l]) {
          miss?.languages.add(languageLabel?.[l]);
          miss?.languageAbbreviation.add(l);
          selected?.country.add(NationsLab?.[country]);
        }
      });
    } else {
      if (
        !languages?.includes(language) &&
        languageLabel?.[language as LanguageEnum]
      ) {
        miss?.languages.add(languageLabel?.[language as LanguageEnum]);
        miss?.languageAbbreviation.add(language);
        selected?.country.add(NationsLab?.[country]);
      }
    }
  });
  languages?.forEach((language) => {
    const country = languageToCountryMap?.[language];
    if (country?.includes(",")) {
      const countryArr = country?.split(",");
      countryArr?.forEach((c: string) => {
        if (!location?.includes(c) && NationsLab?.[c]) {
          miss?.country.add(NationsLab?.[c]);
          miss?.countryAbbreviation.add(c);
          selected?.languages.add(languageLabel?.[language as LanguageEnum]);
        }
      });
    } else {
      if (!location?.includes(country) && NationsLab?.[country]) {
        miss?.country.add(NationsLab?.[country]);
        miss?.countryAbbreviation.add(country);
        selected?.languages.add(languageLabel?.[language as LanguageEnum]);
      }
    }
  });
  return {
    miss: {
      country: Array.from(miss?.country),
      languages: Array.from(miss?.languages),
      languageAbbreviation: Array.from(miss?.languageAbbreviation),
      countryAbbreviation: Array.from(miss?.countryAbbreviation),
    },
    selected: {
      country: Array.from(selected?.country),
      languages: Array.from(selected?.languages),
    },
  };
};

export enum AdsCampaignMatchingAccuracy {
  FOCUS_MODE = "Focus Mode",
  SCALE_MODE = "Scale Mode",
  MAX_SCALE_MODE = "Max Scale Mode",
}

// 根据内容形式获取平台
const getContentFormatToPlatformMap = (t: any) => ({
  [PlatformContentEnum.YOUTUBE_DEDICATED]: t("common.YouTube"),
  [PlatformContentEnum.YOUTUBE_INTEGRATED]: t("common.YouTube"),
  [PlatformContentEnum.TIKTOK_VIDEO]: t("common.TikTok"),
  [PlatformContentEnum.INSTAGRAM_REEL]: t("common.Instagram"),
  [PlatformContentEnum.INSTAGRAM_CAROUSEL_POST]: t("common.Instagram"),
  [PlatformContentEnum.TWITTER_POST]: t("common.Twitter"),
  [PlatformContentEnum.LINKEDIN_POST]: t("common.LinkedIn"),
  [PlatformContentEnum.LINKEDIN_IMAGE_POST]: t("common.LinkedIn"),
  [PlatformContentEnum.LINKEDIN_VIDEO_POST]: t("common.LinkedIn"),
});

export const getPlatformByContentFormat = (placements: string[], t: any) => {
  const res = new Set();
  placements?.forEach((item) => {
    res.add(
      getContentFormatToPlatformMap(t)?.[
        item as keyof typeof getContentFormatToPlatformMap
      ],
    );
  });
  return Array.from(res);
};

export const getBudgetOptions = () => [
  { label: "2,000", value: 2000 },
  { label: "3,000", value: 3000 },
  { label: "4,000", value: 4000 },
  { label: "5,000", value: 5000 },
  { label: "10,000", value: 10000 },
  { label: "20,000", value: 20000 },
  { label: "30,000", value: 30000 },
  { label: "50,000", value: 50000 },
  { label: "100,000", value: 100000 },
  { label: "200,000", value: 200000 },
  { label: "500,000", value: 500000 },
  { label: "1,000,000", value: 1000000 },
];

export enum expectedMaxCpmTypeEnum {
  AUTO = "auto",
  MANUAL = "manual",
}

export enum RadioValueEnum {
  max = "max",
  min = "min",
}

export enum BudgetPerInfluencerTypeEnum {
  max = "Highest budget per influencer",
  min = "Lowest budget per influencer",
}

export enum ProductPhysicalSelectEnum {
  ship = "Shipping",
  video = "ShareVideo",
}

export enum PriorityEnum {
  HIGH = "high",
  LOW = "low",
}

export enum AudienceSizeEnum {
  LARGE = "large",
  MEDIUM = "medium",
  SMALL = "small",
}

export enum AudienceModeType {
  AUTO = "auto",
  MANUAL = "manual",
}

export const prioritizedLab: {
  [key in PriorityEnum]: { label: string; Icon: any };
} = {
  [PriorityEnum.HIGH]: {
    label: "Highly prioritized",
    Icon: PriorityHighlyIcon,
  },
  [PriorityEnum.LOW]: {
    label: "Prioritized",
    Icon: PriorityPrioritizedIcon,
  },
};

export const audienceLab: {
  [key in AudienceSizeEnum]: { label: string; Icon: any };
} = {
  [AudienceSizeEnum.LARGE]: {
    label: "Large-sized audience",
    Icon: AudienceSizeLargeIcon,
  },
  [AudienceSizeEnum.MEDIUM]: {
    label: "Medium-sized audience",
    Icon: AudienceSizeMediumIcon,
  },
  [AudienceSizeEnum.SMALL]: {
    label: "Small-sized audience",
    Icon: AudienceSizeSmallIcon,
  },
};

export enum PaymentPlanEnum {
  SUBSCRIPTION = "subscription",
  PAY_AS_YOU_GO = "pay_as_you_go",
}
